import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles,
  Loader2,
  Car,
  Palette,
  ArrowRight,
  RotateCcw,
  Upload,
  ImageIcon,
  X,
  Wand2,
} from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { useI18n } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_MB = 10;
const ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif";

export const StyleAdvisor = ({ onQuote }) => {
  const { t } = useI18n();
  const fileRef = useRef(null);
  const [vehicle, setVehicle] = useState("");
  const [description, setDescription] = useState("");
  const [photo, setPhoto] = useState(null); // { file, previewUrl }
  const [loading, setLoading] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [result, setResult] = useState(null);
  const [rendered, setRendered] = useState(null); // data URL

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(t("advisor.errFileSize"));
      e.target.value = "";
      return;
    }
    setPhoto({ file, previewUrl: URL.createObjectURL(file) });
    setRendered(null);
  };

  const clearPhoto = () => {
    if (photo?.previewUrl) URL.revokeObjectURL(photo.previewUrl);
    setPhoto(null);
    setRendered(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!vehicle.trim() || !description.trim()) {
      toast.error(t("advisor.errRequired"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/style-advisor`, {
        vehicle: vehicle.trim(),
        description: description.trim(),
      });
      setResult(data);
    } catch (err) {
      toast.error(t("advisor.errGeneric"));
    } finally {
      setLoading(false);
    }
  };

  const render = async () => {
    if (!photo || !result) return;
    setRendering(true);
    try {
      const fd = new FormData();
      fd.append("photo", photo.file);
      fd.append("color", result.color);
      fd.append("finish", result.finish);
      fd.append("vinyl_brand", result.vinyl_brand || "");
      const { data } = await axios.post(`${API}/style-advisor/render`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      setRendered(data.image);
    } catch (err) {
      toast.error(t("advisor.errRender"));
    } finally {
      setRendering(false);
    }
  };

  const reset = () => {
    setResult(null);
    setVehicle("");
    setDescription("");
    setRendered(null);
    clearPhoto();
  };

  const inputCls =
    "bg-[#0F1115] border-white/10 text-white placeholder:text-neutral-600 h-12 rounded-xl focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-0 focus-visible:border-cyan-400";

  return (
    <section
      id="style-advisor"
      data-testid="style-advisor-section"
      className="relative py-24 md:py-32 px-6 md:px-10 overflow-hidden"
    >
      <div className="absolute inset-0 radial-cyan pointer-events-none opacity-50" />
      <div className="relative max-w-[1200px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <span className="font-mono-accent text-xs uppercase tracking-[0.3em] text-cyan inline-flex items-center gap-2">
              <Sparkles size={14} /> {t("advisor.tag")}
            </span>
            <h2 className="font-display font-700 uppercase tracking-tighter text-4xl md:text-6xl mt-4 leading-none">
              {t("advisor.title1")}{" "}
              <span className="text-cyan neon-text-glow">{t("advisor.title2")}</span>
            </h2>
          </div>
          <p className="max-w-md text-neutral-400 font-light">{t("advisor.subtitle")}</p>
        </div>

        <div className="rounded-3xl border border-cyan-500/25 bg-[#0B0D10] overflow-hidden">
          <AnimatePresence mode="wait">
            {!result ? (
              <motion.form
                key="form"
                data-testid="style-advisor-form"
                onSubmit={submit}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="grid grid-cols-1 md:grid-cols-2 gap-0"
              >
                <div className="p-8 md:p-10 border-b md:border-b-0 md:border-r border-white/5 space-y-5">
                  <div className="space-y-2">
                    <Label className="text-neutral-300 text-xs uppercase tracking-wider inline-flex items-center gap-2">
                      <Car size={13} className="text-cyan" /> {t("advisor.vehicle")}
                    </Label>
                    <Input
                      data-testid="advisor-vehicle"
                      value={vehicle}
                      onChange={(e) => setVehicle(e.target.value)}
                      placeholder={t("advisor.vehiclePh")}
                      className={inputCls}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-neutral-300 text-xs uppercase tracking-wider inline-flex items-center gap-2">
                      <Palette size={13} className="text-cyan" /> {t("advisor.description")}
                    </Label>
                    <Textarea
                      data-testid="advisor-description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={t("advisor.descriptionPh")}
                      className={`${inputCls} h-32 py-3`}
                    />
                  </div>

                  {/* Photo upload */}
                  <div className="space-y-2">
                    <Label className="text-neutral-300 text-xs uppercase tracking-wider inline-flex items-center gap-2">
                      <ImageIcon size={13} className="text-cyan" /> {t("advisor.photo")}{" "}
                      <span className="text-neutral-600 normal-case tracking-normal">
                        {t("advisor.photoOptional")}
                      </span>
                    </Label>
                    <input
                      ref={fileRef}
                      type="file"
                      accept={ACCEPT}
                      onChange={onPickFile}
                      className="hidden"
                      data-testid="advisor-file-input"
                    />
                    {!photo ? (
                      <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        data-testid="advisor-file-btn"
                        className="w-full inline-flex items-center justify-center gap-3 border border-dashed border-white/15 text-neutral-400 hover:text-cyan hover:border-cyan-400 rounded-xl py-4 transition-colors duration-300"
                      >
                        <Upload size={16} /> {t("advisor.photoCta")}
                      </button>
                    ) : (
                      <div
                        data-testid="advisor-photo-preview"
                        className="relative rounded-xl border border-cyan-400/30 overflow-hidden bg-black"
                      >
                        <img
                          src={photo.previewUrl}
                          alt="preview"
                          className="w-full h-40 object-cover"
                        />
                        <button
                          type="button"
                          onClick={clearPhoto}
                          data-testid="advisor-photo-clear"
                          className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/70 border border-white/20 flex items-center justify-center text-white hover:bg-black/90"
                          aria-label="remove"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    )}
                    <p className="text-[10px] text-neutral-600 font-mono-accent uppercase tracking-widest">
                      {t("advisor.photoHint")}
                    </p>
                  </div>

                  <button
                    type="submit"
                    data-testid="advisor-submit"
                    disabled={loading}
                    className="w-full inline-flex items-center justify-center gap-3 text-black font-medium text-base py-3.5 rounded-xl neon-glow hover:brightness-110 transition-all duration-300 disabled:opacity-60"
                    style={{ backgroundColor: "#C9A96A" }}
                  >
                    {loading ? (
                      <>
                        <Loader2 size={18} className="animate-spin" /> {t("advisor.thinking")}
                      </>
                    ) : (
                      <>
                        <Sparkles size={16} /> {t("advisor.submit")}
                      </>
                    )}
                  </button>

                  <p className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-neutral-500">
                    {t("advisor.poweredBy")}
                  </p>
                </div>

                <div className="p-8 md:p-10 bg-[#0F1115] flex items-center justify-center min-h-[280px]">
                  <div className="text-center max-w-sm">
                    <div className="mx-auto h-14 w-14 rounded-full border border-cyan-500/30 bg-cyan-400/5 flex items-center justify-center">
                      <Sparkles size={22} className="text-cyan" />
                    </div>
                    <p className="text-neutral-400 font-light mt-5 text-sm md:text-base leading-relaxed">
                      {t("advisor.emptyState")}
                    </p>
                  </div>
                </div>
              </motion.form>
            ) : (
              <motion.div
                key="result"
                data-testid="style-advisor-result"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="grid grid-cols-1 md:grid-cols-5 gap-0"
              >
                <div className="md:col-span-3 p-8 md:p-12 border-b md:border-b-0 md:border-r border-white/5">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={14} className="text-cyan" />
                    <span className="font-mono-accent text-[10px] uppercase tracking-[0.28em] text-cyan">
                      {t("advisor.resultTag")}
                    </span>
                  </div>

                  <h3
                    data-testid="advisor-result-color"
                    className="font-display font-700 uppercase tracking-tighter text-3xl md:text-5xl leading-none mt-2"
                  >
                    {result.color}
                  </h3>

                  <div className="mt-6 flex flex-wrap gap-3">
                    <span
                      data-testid="advisor-result-finish"
                      className="inline-flex items-center gap-2 rounded-full border border-cyan-400/40 bg-cyan-400/10 px-4 py-1.5 text-cyan font-mono-accent text-[11px] uppercase tracking-[0.2em]"
                    >
                      {t("advisor.finishLabel")} · {result.finish}
                    </span>
                    <span
                      data-testid="advisor-result-brand"
                      className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-4 py-1.5 text-neutral-200 font-mono-accent text-[11px] uppercase tracking-[0.2em]"
                    >
                      {t("advisor.brandLabel")} · {result.vinyl_brand}
                    </span>
                  </div>

                  <p
                    data-testid="advisor-result-reasoning"
                    className="mt-6 text-neutral-300 font-light text-base leading-relaxed"
                  >
                    {result.reasoning}
                  </p>

                  <div className="mt-6 rounded-2xl border-l-2 border-cyan-400 bg-white/[0.02] px-5 py-4">
                    <span className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-cyan/80">
                      {t("advisor.complementaryLabel")}
                    </span>
                    <p
                      data-testid="advisor-result-complementary"
                      className="text-neutral-200 font-light mt-1"
                    >
                      {result.complementary}
                    </p>
                  </div>

                  <div className="mt-8 flex flex-wrap items-center gap-3">
                    <button
                      data-testid="advisor-cta-quote"
                      onClick={() => onQuote && onQuote()}
                      className="inline-flex items-center gap-2 text-black font-medium text-sm px-6 py-3 rounded-full neon-glow hover:scale-[1.03] active:scale-95 transition-transform duration-300"
                      style={{ backgroundColor: "#C9A96A" }}
                    >
                      {t("advisor.ctaQuote")} <ArrowRight size={14} />
                    </button>
                    <button
                      data-testid="advisor-reset"
                      onClick={reset}
                      className="inline-flex items-center gap-2 border border-white/15 text-neutral-300 font-medium text-sm px-5 py-3 rounded-full hover:border-cyan-400 hover:text-cyan transition-colors duration-300"
                    >
                      <RotateCcw size={14} /> {t("advisor.retry")}
                    </button>
                  </div>
                </div>

                {/* Render panel */}
                <div className="md:col-span-2 p-6 md:p-8 bg-[#0F1115] flex flex-col">
                  <div className="flex items-center gap-2 mb-4">
                    <Wand2 size={14} className="text-cyan" />
                    <span className="font-mono-accent text-[10px] uppercase tracking-[0.28em] text-cyan">
                      {t("advisor.renderTag")}
                    </span>
                  </div>

                  {rendered ? (
                    <div
                      data-testid="advisor-rendered"
                      className="relative rounded-2xl border border-cyan-400/40 overflow-hidden bg-black"
                    >
                      <img
                        src={rendered}
                        alt={t("advisor.renderAlt")}
                        className="w-full h-auto object-cover"
                      />
                      <span className="absolute bottom-3 left-3 inline-flex items-center gap-2 rounded-full bg-black/70 border border-cyan-400/40 px-3 py-1 font-mono-accent text-[9px] uppercase tracking-[0.22em] text-cyan">
                        <Sparkles size={10} /> {t("advisor.renderBadge")}
                      </span>
                    </div>
                  ) : photo ? (
                    <div className="flex flex-col gap-4 flex-1">
                      <div className="relative rounded-2xl border border-white/10 overflow-hidden bg-black">
                        <img
                          src={photo.previewUrl}
                          alt="original"
                          className="w-full h-56 object-cover opacity-90"
                        />
                        <span className="absolute bottom-2 left-2 font-mono-accent text-[9px] uppercase tracking-[0.22em] bg-black/70 border border-white/20 rounded-full px-3 py-1 text-neutral-300">
                          {t("advisor.original")}
                        </span>
                      </div>
                      <button
                        data-testid="advisor-render-btn"
                        onClick={render}
                        disabled={rendering}
                        className="w-full inline-flex items-center justify-center gap-3 text-black font-medium text-sm py-3.5 rounded-xl neon-glow hover:brightness-110 transition-all duration-300 disabled:opacity-60"
                        style={{ backgroundColor: "#C9A96A" }}
                      >
                        {rendering ? (
                          <>
                            <Loader2 size={16} className="animate-spin" /> {t("advisor.rendering")}
                          </>
                        ) : (
                          <>
                            <Wand2 size={15} /> {t("advisor.renderCta")}
                          </>
                        )}
                      </button>
                      <p className="text-[10px] text-neutral-500 font-mono-accent uppercase tracking-widest text-center">
                        {t("advisor.renderPoweredBy")}
                      </p>
                    </div>
                  ) : (
                    <div className="flex-1 flex items-center justify-center rounded-2xl border border-dashed border-white/10 p-6 text-center">
                      <div>
                        <div className="mx-auto h-12 w-12 rounded-full border border-cyan-500/30 bg-cyan-400/5 flex items-center justify-center">
                          <ImageIcon size={18} className="text-cyan" />
                        </div>
                        <p className="text-neutral-400 font-light mt-4 text-sm">
                          {t("advisor.renderNoPhoto")}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
